import {
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { GripVertical } from "lucide-react";
import type { KeyboardEvent, PointerEvent, ReactNode } from "react";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";

export function useSortableSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
}

export function reorderByDragId<T>(
  fields: T[],
  dragIdOf: (field: T) => string,
  { active, over }: DragEndEvent,
  move: (from: number, to: number) => void,
) {
  if (!over || active.id === over.id) return;
  const oldIndex = fields.findIndex((field) => dragIdOf(field) === active.id);
  const newIndex = fields.findIndex((field) => dragIdOf(field) === over.id);
  if (oldIndex >= 0 && newIndex >= 0) move(oldIndex, newIndex);
}

export function SortableItem({
  id,
  label,
  className,
  children,
}: {
  id: string;
  label: string;
  className?: string;
  children: ReactNode | ((handle: ReactNode) => ReactNode);
}) {
  const draggable = useDraggable({ id });
  const droppable = useDroppable({ id });
  const hasVisibleHandle = typeof children === "function";
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (
      event.target instanceof HTMLElement &&
      event.target.closest(
        "button, input, textarea, select, a, [contenteditable='true']",
      )
    ) {
      return;
    }
    draggable.listeners?.onPointerDown?.(event);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    draggable.listeners?.onKeyDown?.(event);
  };

  return (
    <div
      ref={(node) => {
        draggable.setNodeRef(node);
        droppable.setNodeRef(node);
      }}
      className={cn(
        "touch-none",
        className,
        draggable.isDragging && "opacity-40",
        droppable.isOver && !draggable.isDragging && "ring-2 ring-primary/40",
      )}
      onPointerDown={onPointerDown}
    >
      {hasVisibleHandle ? (
        children(
          <Button
            ref={draggable.setActivatorNodeRef}
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            className="text-muted-foreground hover:bg-transparent! hover:text-muted-foreground!"
            {...draggable.attributes}
            {...draggable.listeners}
          >
            <GripVertical />
          </Button>,
        )
      ) : (
        <>
          <button
            ref={draggable.setActivatorNodeRef}
            type="button"
            className="sr-only"
            aria-label={label}
            {...draggable.attributes}
            onKeyDown={onKeyDown}
          />
          {children}
        </>
      )}
    </div>
  );
}
